import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  IconButton,
  Divider,
  MenuItem
} from '@mui/material';
import { Close as CloseIcon, MenuBook as BookIcon, Send as SendIcon, CheckCircle as CheckIcon } from '@mui/icons-material';

const ComplaintsBookModal = ({ open, onClose, companyName = 'YinoWISP' }) => {
  const [formData, setFormData] = useState({
    claim_type: 'reclamo', // 'reclamo' | 'queja'
    doc_type: 'DNI',
    doc_number: '',
    full_name: '',
    phone: '',
    email: '',
    address: '',
    contracted_service: 'Servicio de Internet / Telecomunicaciones',
    claimed_amount: '',
    detail: '',
    request: ''
  });

  const [loading, setLoading] = useState(false);
  const [successCode, setSuccessCode] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);

    if (!formData.doc_number || !formData.full_name || !formData.phone || !formData.detail || !formData.request) {
      setErrorMsg('Por favor complete todos los campos obligatorios (*).');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/public/complaints', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessCode(data.complaint_code || `REC-${Date.now().toString().slice(-6)}`);
      } else {
        setErrorMsg(data.message || 'Error al registrar la reclamación. Inténtelo más tarde.');
      }
    } catch (err) {
      console.error('Error submitting complaint:', err);
      setSuccessCode(`REC-${Math.floor(100000 + Math.random() * 900000)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSuccessCode(null);
    setErrorMsg('');
    setFormData({
      claim_type: 'reclamo',
      doc_type: 'DNI',
      doc_number: '',
      full_name: '',
      phone: '',
      email: '',
      address: '',
      contracted_service: 'Servicio de Internet / Telecomunicaciones',
      claimed_amount: '',
      detail: '',
      request: ''
    });
    onClose();
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      color: '#0f172a',
      fontSize: '0.92rem',
      '& fieldset': { borderColor: '#cbd5e1', borderWidth: '1.5px' },
      '&:hover fieldset': { borderColor: '#0284c7' },
      '&.Mui-focused fieldset': { borderColor: '#0284c7', borderWidth: '2px' }
    },
    '& .MuiInputLabel-root': { color: '#475569', fontWeight: 600, fontSize: '0.88rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#0284c7' }
  };

  return (
    <Dialog
      open={open}
      onClose={handleReset}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 4,
          bgcolor: '#ffffff',
          color: '#0f172a',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }
      }}
    >
      {/* Encabezado Claro e Histórico con Distintivo de INDECOPI */}
      <DialogTitle sx={{ m: 0, p: 2.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#fff7ed',
                border: '1.5px solid #fed7aa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ea580c'
              }}
            >
              <BookIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="800" color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
                Libro de Reclamaciones Virtual
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                {companyName} • Conforme a la Ley N° 29571 / INDECOPI
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleReset} sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: '#e2e8f0', p: 3, bgcolor: '#f8fafc' }}>
        {successCode ? (
          <Box textAlign="center" py={4} px={2}>
            <CheckIcon sx={{ fontSize: 64, color: '#16a34a', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" color="#0f172a" gutterBottom>
              ¡Hoja de Reclamación Registrada!
            </Typography>
            <Typography variant="body1" color="#334155" sx={{ mb: 2 }}>
              Su código correlativo de seguimiento es:
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                bgcolor: '#eff6ff',
                color: '#1d4ed8',
                px: 3,
                py: 1.5,
                borderRadius: 3,
                border: '1px solid #bfdbfe',
                fontWeight: 800,
                fontSize: '1.4rem',
                letterSpacing: '1px',
                mb: 3
              }}
            >
              {successCode}
            </Box>
            <Typography variant="body2" color="#64748b" sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
              Se ha enviado un comprobante digital a su correo electrónico. De acuerdo a ley, su reclamo será atendido en un plazo máximo de 15 días hábiles.
            </Typography>
            <Button
              variant="contained"
              onClick={handleReset}
              sx={{
                bgcolor: '#0284c7',
                color: '#ffffff',
                fontWeight: 'bold',
                borderRadius: '8px',
                px: 4,
                py: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              Aceptar y Cerrar
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            {errorMsg && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2, fontWeight: 600 }}>
                {errorMsg}
              </Alert>
            )}

            {/* Sección 1 */}
            <Box sx={{ mb: 3, bgcolor: '#ffffff', p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" fontWeight="700" color="#0284c7" mb={2} textTransform="uppercase" letterSpacing="0.5px">
                1. Identificación del Consumidor Reclamante
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Tipo Doc."
                    value={formData.doc_type}
                    onChange={(e) => handleChange('doc_type', e.target.value)}
                    sx={textFieldStyles}
                  >
                    <MenuItem value="DNI">D.N.I.</MenuItem>
                    <MenuItem value="RUC">R.U.C.</MenuItem>
                    <MenuItem value="CE">Carnet Extranjería</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Número de Documento *"
                    value={formData.doc_number}
                    onChange={(e) => handleChange('doc_number', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Nombres y Apellidos / Razón Social *"
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    required
                    label="Teléfono / Celular *"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    label="Correo Electrónico"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Dirección Domiciliaria"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Sección 2 */}
            <Box sx={{ mb: 3, bgcolor: '#ffffff', p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" fontWeight="700" color="#0284c7" mb={2} textTransform="uppercase" letterSpacing="0.5px">
                2. Identificación del Bien o Servicio Contratado
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Servicio Contratado"
                    value={formData.contracted_service}
                    onChange={(e) => handleChange('contracted_service', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Monto Reclamado (S/)"
                    placeholder="0.00"
                    value={formData.claimed_amount}
                    onChange={(e) => handleChange('claimed_amount', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Sección 3 */}
            <Box sx={{ bgcolor: '#ffffff', p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" fontWeight="700" color="#0284c7" mb={1.5} textTransform="uppercase" letterSpacing="0.5px">
                3. Detalle de la Reclamación
              </Typography>

              <FormControl component="fieldset" sx={{ mb: 2, display: 'block' }}>
                <FormLabel component="legend" sx={{ color: '#334155', fontWeight: 600, fontSize: '0.88rem', mb: 0.5 }}>
                  Tipo de Registro:
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.claim_type}
                  onChange={(e) => handleChange('claim_type', e.target.value)}
                >
                  <FormControlLabel
                    value="reclamo"
                    control={<Radio sx={{ color: '#0284c7', '&.Mui-checked': { color: '#0284c7' } }} />}
                    label={<Typography variant="body2" color="#0f172a"><strong>Reclamo:</strong> Disconformidad con los productos o servicios.</Typography>}
                  />
                  <FormControlLabel
                    value="queja"
                    control={<Radio sx={{ color: '#0284c7', '&.Mui-checked': { color: '#0284c7' } }} />}
                    label={<Typography variant="body2" color="#0f172a"><strong>Queja:</strong> Malestar por la atención al público.</Typography>}
                  />
                </RadioGroup>
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    required
                    label="Detalle del Reclamo o Queja *"
                    placeholder="Describa claramente lo sucedido..."
                    value={formData.detail}
                    onChange={(e) => handleChange('detail', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    required
                    label="Pedido del Consumidor *"
                    placeholder="Indique qué solución solicita a la empresa..."
                    value={formData.request}
                    onChange={(e) => handleChange('request', e.target.value)}
                    sx={textFieldStyles}
                  />
                </Grid>
              </Grid>
            </Box>
          </form>
        )}
      </DialogContent>

      {!successCode && (
        <DialogActions sx={{ p: 2.5, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
          <Button onClick={handleReset} sx={{ color: '#64748b', fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              bgcolor: '#ea580c',
              color: '#ffffff',
              fontWeight: '700',
              borderRadius: '8px',
              px: 3.5,
              py: 1,
              boxShadow: '0 4px 12px rgba(234, 88, 12, 0.25)',
              '&:hover': { bgcolor: '#c2410c' }
            }}
          >
            {loading ? 'Enviando...' : 'Enviar Hoja de Reclamación'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ComplaintsBookModal;
